// Chat-related type definitions

export interface ChatThread {
  id: string;
  userId?: string;
  tenantId?: string;
  title: string;
  createdAt: Date;
  updatedAt: Date;
  metadata?: Record<string, any>;
  isShared?: boolean;
  sharedWithGroups?: string[];
  isArchived?: boolean;
  isPinned?: boolean;
  pinnedAt?: Date | null;
  messages: ChatMessage[];
  currentMode?: string;
  modeHistory?: Array<{ mode: string; previousMode?: string; timestamp: string }>;
}

export interface ChatMessage {
  id: string;
  threadId?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: Record<string, any>;
  createdAt?: Date;
  timestamp?: Date;
  sources?: MessageSource[];
  searchResults?: {
    query: string;
    results: SearchResult[];
  };
  mode?: string;
  modeSwitched?: boolean;
  newMode?: string;
  groundingStatus?: string;
  verificationSummary?: VerificationSummary;
  webSearchStatus?: WebSearchStatus;
}

export interface WebSearchStatus {
  requested: boolean;
  status: 'disabled' | 'enabled' | 'error';
  reason?: string;
  provider?: string | null;
  query?: string;
  message?: string;
}

export interface SearchResult {
  id: string;
  title: string;
  snippet: string;
  excerpt?: string;
  fileName: string;
  relevanceScore: number;
  rawRelevanceScore?: number;
  relevanceLabel?: 'high' | 'medium' | 'low';
  relevanceSource?: string;
  citationIndex?: number;
  selected?: boolean;
  pageNumber?: number;
  segmentId?: string;
}

export interface MessageSource {
  id: string;
  messageId: string;
  sourceType: 'document' | 'web' | 'knowledge_base';
  sourceId?: string;
  sourceUrl?: string;
  relevanceScore?: number;
  excerpt?: string;
  createdAt: Date;
}

export interface CreateThreadParams {
  userId: string;
  tenantId: string;
  title?: string;
  metadata?: Record<string, any>;
}

export interface SendMessageResponse {
  message_id: string;
  status: 'completed' | 'processing' | 'failed';
}

export interface SummaryParams {
  fileIds: string[];
  summaryType: 'executive' | 'technical' | 'comprehensive';
  language?: 'ja' | 'en';
  maxLength?: number;
}

export interface SourceReference {
  fileId: string;
  excerpt?: string;
  relevanceScore?: number;
}

export interface DocumentSummary {
  text: string;
  keyPoints: string[];
  sources: SourceReference[];
  generatedAt: Date;
  language?: string;
}

export interface ChatSettings {
  userId: string;
  defaultLanguage: 'ja' | 'en';
  autoSuggest: boolean;
  streamResponses: boolean;
  maxContextMessages: number;
  settings?: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export interface ChatError {
  code: string;
  message: string;
  details?: any;
}

// ============================================================
// Verification Types (Sprint 10 — Self-Verification Stage 1)
// ============================================================

export interface VerificationFlag {
  type: string;
  message: string;
  severity: 'ok' | 'caution' | 'warning';
  details?: Record<string, any>;
}

export interface SentenceAnnotation {
  text: string;
  status: 'supported' | 'unsupported' | 'neutral';
}

export interface VerificationSummary {
  level: 'ok' | 'caution' | 'warning';
  supported_claims: number;
  uncited_claims: number;
  weak_citations: number;
  flags: VerificationFlag[];
  sentence_annotations?: SentenceAnnotation[];
}

// ============================================================
// Chat Mode Types (Issue #302)
// ============================================================

/**
 * Chat mode definition
 */
export interface ChatMode {
  id: string;
  name: string;
  description?: string;
  color: string;
  icon?: string;
  isActive: boolean;
  isCustom: boolean;
  sortOrder: number;
  settings: Record<string, any>;
}

/**
 * Mode context for mode switching
 */
export interface ModeContext {
  modeId: string;
  threadId: string;
  userId?: string;
  tenantId?: string;
  previousMode?: string;
  modeData?: Record<string, any>;
}

/**
 * Mode list response from API
 */
export interface ModeListResponse {
  modes: ChatMode[];
  total: number;
}

/**
 * Switch mode request
 */
export interface SwitchModeRequest {
  mode_id: string;
  mode_data?: Record<string, any>;
}

/**
 * Switch mode response
 */
export interface SwitchModeResponse {
  mode: ChatMode;
  message: string;
  previous_mode?: string;
}

/**
 * Thread mode response
 */
export interface ThreadModeResponse {
  thread_id: string;
  current_mode: string;
  mode: ChatMode;
}

/**
 * Slash command definition for mode switching
 */
export interface SlashCommand {
  command: string;
  modeId: string;
  label: string;
  description: string;
  icon?: string;
}

/**
 * Available slash commands
 */
export const SLASH_COMMANDS: SlashCommand[] = [
  {
    command: '/help',
    modeId: 'help',
    label: 'ヘルプ',
    description: 'システムの使い方とコマンド一覧',
    icon: 'help',
  },
  {
    command: '/settings',
    modeId: 'settings',
    label: '設定',
    description: 'データソース状態・利用統計',
    icon: 'settings',
  },
  {
    command: '/ask',
    modeId: 'ask',
    label: 'Q&A',
    description: '質問応答モード（デフォルト）',
    icon: 'question_answer',
  },
  {
    command: '/plan',
    modeId: 'plan',
    label: '設計深掘り',
    description: '設計に関する深い議論',
    icon: 'architecture',
  },
  {
    command: '/drbfm',
    modeId: 'drbfm',
    label: 'DRBFM分析',
    description: 'DRBFM分析サポート',
    icon: 'assessment',
  },
  {
    command: '/verification',
    modeId: 'verification',
    label: '設計検証',
    description: '設計検証項目の分析・改善支援',
    icon: 'verified',
  },
  {
    command: '/cad',
    modeId: 'cad',
    label: 'CAD分析',
    description: 'CADデータの構造・設計レビュー',
    icon: 'view_in_ar',
  },
];

/**
 * Mode color map for UI styling
 */
export const MODE_COLORS: Record<string, string> = {
  help: '#4caf50',
  settings: '#ff9800',
  ask: '#1976d2',
  plan: '#9c27b0',
  drbfm: '#f44336',
  verification: '#00897b',
  cad: '#607d8b',
};
